import React, { useEffect, useState, useCallback, useRef } from "react";
import { Hooks, Util, Storage, Context } from "@xt/xtbase";
import { Drawer } from "antd";
import FiatCurrencyIcon from "@/components/Pages/Convert/FiatCurrencyIcon";
import { getFiatCurrencyIconSvg } from "@/components/Pages/Convert/fiatCurrencyIcons";
import { getFiats } from "@/api/v4/payment";
import { resolveThirdPartyFiatIconUrl } from "@/constants/thirdPartyFiatIconMap";
import { openThirdPartyBuy, getThirdPartyBuyUrl } from "@/utils/openThirdPartyBuy";
import { getRecommendedFiatCodesFromList } from "@/utils/fiatMarket";
import {
  trackDepositPageView,
  trackDepositMethodSelect,
  trackDepositAbandon,
  mapDepositOptionIdToType,
} from "@/utils/clientBehavior";
import styles from "./index.module.less";

const { useTranslation } = Hooks;
const { getUrl } = Util;
const { XtContext } = Context;
const FIAT_PLATE_ID = 258;
const PRIORITY_FIAT_CODES = ["BRL", "SAR", "AED", "VND"];
const PRIORITY_FIAT_CODE_SET = new Set(PRIORITY_FIAT_CODES);
const FIAT_BG_LIST = ["#00C19A", "#1781D4", "#F89D4C", "#5985ED", "#B9F78A"];

function getFiatCodesFromCurrencyList(currencyList) {
  const currencies = currencyList?.currencies || currencyList?.currencyList?.currencies || [];
  if (!Array.isArray(currencies)) return [];
  return currencies.reduce((result, item) => {
    const code = String(item?.currency || item?.coinName || item?.currencyCode || "").toUpperCase();
    const plates = Array.isArray(item?.plates) ? item.plates : [];
    const isFiat = plates.some((plateId) => Number(plateId) === FIAT_PLATE_ID);
    if (isFiat && code && !result.includes(code)) result.push(code);
    return result;
  }, []);
}

function uniqueUpperCodes(codes = []) {
  const result = [];
  const seen = new Set();
  codes.forEach((item) => {
    const code = String(item || "").toUpperCase();
    if (!code || seen.has(code)) return;
    seen.add(code);
    result.push(code);
  });
  return result;
}

function sortWithPriority(codes = []) {
  const prioritySet = new Set(PRIORITY_FIAT_CODES);
  const order = new Map(PRIORITY_FIAT_CODES.map((code, index) => [code, index]));
  const priority = [];
  const normal = [];
  codes.forEach((code) => {
    if (prioritySet.has(code)) {
      priority.push(code);
    } else {
      normal.push(code);
    }
  });
  priority.sort((a, b) => (order.get(a) || 0) - (order.get(b) || 0));
  return [...priority, ...normal];
}

function resolveHomeDepositFiatIconUrl(code) {
  const upper = String(code || "").toUpperCase();
  // BRL/SAR/AED/VND 沿用当前 FiatCurrencyIcon 视觉
  if (!upper || PRIORITY_FIAT_CODE_SET.has(upper)) return "";
  return resolveThirdPartyFiatIconUrl(upper, null, null);
}

function getFiatColorByIndex(index) {
  const i = Number.isFinite(index) ? index : 0;
  return FIAT_BG_LIST[i % FIAT_BG_LIST.length];
}

function getFiatSymbolByCode(code) {
  const upper = String(code || "").toUpperCase();
  return upper.charAt(0) || "";
}

const FiatDepositSheetIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="23" viewBox="0 0 25 23" fill="none">
    <path d="M2.55556 23C1.85278 23 1.25116 22.7498 0.750694 22.2493C0.250231 21.7488 0 21.1472 0 20.4444V2.55556C0 1.85278 0.250231 1.25116 0.750694 0.750694C1.25116 0.250231 1.85278 0 2.55556 0H20.4444C21.1472 0 21.7488 0.250231 22.2493 0.750694C22.7498 1.25116 23 1.85278 23 2.55556H12.7778C11.2657 2.55556 10.0359 3.0294 9.08819 3.97708C8.14051 4.92477 7.66667 6.15463 7.66667 7.66667V15.3333C7.66667 16.8454 8.14051 18.0752 9.08819 19.0229C10.0359 19.9706 11.2657 20.4444 12.7778 20.4444H23C23 21.1472 22.7498 21.7488 22.2493 22.2493C21.7488 22.7498 21.1472 23 20.4444 23H2.55556ZM12.7778 17.8889C12.075 17.8889 11.4734 17.6387 10.9729 17.1382C10.4725 16.6377 10.2222 16.0361 10.2222 15.3333V7.66667C10.2222 6.96389 10.4725 6.36227 10.9729 5.86181C11.4734 5.36134 12.075 5.11111 12.7778 5.11111H21.7222C22.425 5.11111 23.0266 5.36134 23.5271 5.86181C24.0275 6.36227 24.2778 6.96389 24.2778 7.66667V15.3333C24.2778 16.0361 24.0275 16.6377 23.5271 17.1382C23.0266 17.6387 22.425 17.8889 21.7222 17.8889H12.7778ZM16.6111 13.4167C17.1648 13.4167 17.6227 13.2356 17.9847 12.8736C18.3468 12.5116 18.5278 12.0537 18.5278 11.5C18.5278 10.9463 18.3468 10.4884 17.9847 10.1264C17.6227 9.76435 17.1648 9.58333 16.6111 9.58333C16.0574 9.58333 15.5995 9.76435 15.2375 10.1264C14.8755 10.4884 14.6944 10.9463 14.6944 11.5C14.6944 12.0537 14.8755 12.5116 15.2375 12.8736C15.5995 13.2356 16.0574 13.4167 16.6111 13.4167Z" fill="#4EDEA3"/>
  </svg>
);

const CryptoDepositSheetIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="19" viewBox="0 0 25 19" fill="none">
    <path d="M5 18.75L0 13.75L1.75 11.9375L3.75 13.9375V5C3.75 3.625 4.23958 2.44792 5.21875 1.46875C6.19792 0.489583 7.375 0 8.75 0C10.125 0 11.3021 0.489583 12.2812 1.46875C13.2604 2.44792 13.75 3.625 13.75 5V13.75C13.75 14.4375 13.9948 15.026 14.4844 15.5156C14.974 16.0052 15.5625 16.25 16.25 16.25C16.9375 16.25 17.526 16.0052 18.0156 15.5156C18.5052 15.026 18.75 14.4375 18.75 13.75V4.8125L16.75 6.8125L15 5L20 0L25 5L23.25 6.8125L21.25 4.8125V13.75C21.25 15.125 20.7604 16.3021 19.7812 17.2812C18.8021 18.2604 17.625 18.75 16.25 18.75C14.875 18.75 13.6979 18.2604 12.7188 17.2812C11.7396 16.3021 11.25 15.125 11.25 13.75V5C11.25 4.3125 11.0052 3.72396 10.5156 3.23438C10.026 2.74479 9.4375 2.5 8.75 2.5C8.0625 2.5 7.47396 2.74479 6.98438 3.23438C6.49479 3.72396 6.25 4.3125 6.25 5V13.9375L8.25 11.9375L10 13.75L5 18.75Z" fill="#4EDEA3"/>
  </svg>
);

const ThirdPartyBuySheetIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="20" viewBox="0 0 24 20" fill="none">
    <path d="M2.4 19.2C1.74 19.2 1.175 18.965 0.705 18.495C0.235 18.025 0 17.46 0 16.8V2.4C0 1.74 0.235 1.175 0.705 0.705C1.175 0.235 1.74 0 2.4 0H21.6C22.26 0 22.825 0.235 23.295 0.705C23.765 1.175 24 1.74 24 2.4V16.8C24 17.46 23.765 18.025 23.295 18.495C22.825 18.965 22.26 19.2 21.6 19.2H2.4ZM2.4 9.6H21.6V4.8H2.4V9.6Z" fill="#E9C349"/>
  </svg>
);

const DepositSheetArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M6.75 4.5L11.25 9L6.75 13.5" stroke="rgba(211, 197, 172, 0.35)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DepositSheetChevron = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M4.5 6.25L8 9.75L11.5 6.25" stroke="#707A8A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function DepositFiatIcon({ code, className, optionIndex = 0 }) {
  const upper = String(code || "").toUpperCase();
  if (getFiatCurrencyIconSvg(upper)) {
    return <FiatCurrencyIcon code={upper} className={className} style={{ background: "transparent" }} />;
  }
  const iconUrl = resolveHomeDepositFiatIconUrl(upper);
  if (!iconUrl) {
    return (
      <span
        className={`${className} ${styles["deposit-sheet__currency-fallback"]}`}
        style={{ background: getFiatColorByIndex(optionIndex) }}
      >
        {getFiatSymbolByCode(upper)}
      </span>
    );
  }
  return <img src={iconUrl} alt="" className={`${className} ${styles["deposit-sheet__currency-icon-img"]}`} referrerPolicy="no-referrer" />;
}

/**
 * 首页充值底部弹层：QuickActions 与 AssetBalance 共用。
 * @param {{ open: boolean; onClose: () => void; preferredFiatCode?: string|null }} props
 */
function HomeDepositDrawer({ open, onClose, preferredFiatCode = null }) {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const loginStatus = appState?.loginStatus;
  const [fiatCodesKey, setFiatCodesKey] = useState("");

  const [showFiatDepositOption, setShowFiatDepositOption] = useState(false);
  const [fiatOptionsForDeposit, setFiatOptionsForDeposit] = useState([]);
  const [primaryFiatForDeposit, setPrimaryFiatForDeposit] = useState(null);
  const [fiatDropdownOpen, setFiatDropdownOpen] = useState(false);
  const fiatDropdownWrapRef = useRef(null);
  const depositFlowLeftRef = useRef(false);
  const depositPageViewSentRef = useRef(false);

  const handleDrawerClose = useCallback(() => {
    if (!depositFlowLeftRef.current) {
      trackDepositAbandon({
        depositType: "fiat",
        pagePath: typeof window !== "undefined" ? window.location.pathname : "",
      });
    }
    depositFlowLeftRef.current = false;
    depositPageViewSentRef.current = false;
    onClose();
  }, [onClose]);

  const wrapDepositOptionClick = useCallback((item, originalOnClick) => {
    return () => {
      const depositType = mapDepositOptionIdToType(item.id);
      trackDepositMethodSelect({
        methodId: item.id,
        currency: primaryFiatForDeposit || "USDT",
        depositType,
        pagePath: typeof window !== "undefined" ? window.location.pathname : "",
      });
      depositFlowLeftRef.current = true;
      originalOnClick();
    };
  }, [primaryFiatForDeposit]);

  const syncFiatState = useCallback(async () => {
    const currencyList = Storage.get("currencyList") || {};
    const baseOptions = getFiatCodesFromCurrencyList(currencyList);
    let thirdPartyOptions = [];
    try {
      const fiats = await getFiats({
        params: { tradeType: "buy" },
        isAutoPopError: false,
      });
      thirdPartyOptions = Array.isArray(fiats)
        ? fiats.map((item) => String(item?.fiat || "").toUpperCase()).filter(Boolean)
        : [];
    } catch (_) {}

    const mergedOptions = uniqueUpperCodes([...baseOptions, ...thirdPartyOptions]);
    const optionsForDisplay = sortWithPriority(mergedOptions);
    // 保持现有“优先选中”逻辑：优先从首页基础法币池里选择；为空时才回退到并集池
    const optionsForSelection = baseOptions.length > 0 ? baseOptions : optionsForDisplay;
    const recommendedPayload = Storage.get("recommendedCurrencyList") || {};
    const recommendedCodes = getRecommendedFiatCodesFromList(recommendedPayload.currencies || []);
    setFiatCodesKey(optionsForDisplay.join(","));
    setShowFiatDepositOption(optionsForDisplay.length > 0);
    setFiatOptionsForDeposit(optionsForDisplay);

    const want =
      preferredFiatCode && String(preferredFiatCode).toUpperCase() !== "USDT"
        ? String(preferredFiatCode).toUpperCase()
        : null;
    const recommendedPrimary = recommendedCodes.find((code) => optionsForSelection.includes(code)) || null;
    let primary = recommendedPrimary || optionsForSelection[0] || null;
    if (!recommendedPrimary && want && optionsForSelection.includes(want)) {
      primary = want;
    }
    if (!primary && want && optionsForDisplay.includes(want)) {
      primary = want;
    }
    setPrimaryFiatForDeposit(primary);
  }, [preferredFiatCode]);

  useEffect(() => {
    if (!open) return;
    syncFiatState();
    if (!depositPageViewSentRef.current) {
      depositPageViewSentRef.current = true;
      trackDepositPageView({
        depositType: "fiat",
        pagePath: typeof window !== "undefined" ? window.location.pathname : "",
      });
    }
  }, [open, syncFiatState]);

  useEffect(() => {
    if (!fiatDropdownOpen) return undefined;
    const handleClickOutside = (event) => {
      if (fiatDropdownWrapRef.current && !fiatDropdownWrapRef.current.contains(event.target)) {
        setFiatDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [fiatDropdownOpen]);

  const goCryptoDeposit = () => {
    onClose();
    window.location.href = getUrl("/wallet/account/common/deposit");
  };

  const goFiatDeposit = async () => {
    let code = primaryFiatForDeposit;
    if (!code) {
      code = fiatOptionsForDeposit[0] || null;
    }
    if (!code) return;
    onClose();
    window.location.href = getUrl(`/wallet/account/common/deposit/fiat?currency=${String(code).toLowerCase()}`);
  };

  const fiatCode = primaryFiatForDeposit || "";
  const selectedFiatIndex = fiatOptionsForDeposit.findIndex((code) => String(code || "").toUpperCase() === fiatCode);
  const currentFiatUpper = String(fiatCode || "").toUpperCase();
  const showFiatDepositItem = PRIORITY_FIAT_CODE_SET.has(currentFiatUpper);
  /** AED/BRL/SAR/VND 已有专属法币充值入口，不重复展示三方买币 */
  const hideThirdPartyBuyItem = showFiatDepositItem;

  const goThirdPartyBuy = () => {
    const code = primaryFiatForDeposit || preferredFiatCode;
    const query = {
      currency: code ? String(code).toUpperCase() : undefined,
    };
    onClose();
    if (loginStatus !== 1) {
      const backurl = getThirdPartyBuyUrl(query, { absolute: true });
      window.location.href = getUrl(`/accounts/login?backurl=${encodeURIComponent(backurl)}`);
      return;
    }
    openThirdPartyBuy(query);
  };

  const thirdPartyDepositRow = {
    id: "thirdParty",
    title: t("home.depositSheetBuyUsdtTitle", { currency: fiatCode }) || `Buy USDT with ${fiatCode}`,
    description: t("finance.buchongBuy") || "",
    icon: <ThirdPartyBuySheetIcon />,
    iconTone: "mint",
    onClick: goThirdPartyBuy,
    itemTone: "gold",
  };

  const depositOptions = showFiatDepositOption
    ? [
        ...(showFiatDepositItem
          ? [
              {
                id: "fiat",
                title: t("home.depositSheetFiatTitle", { currency: fiatCode }) || `${fiatCode} ${t("more.deposit") || "Deposit"}`,
                description: t("home.quickDepositFiatDesc") || "Buy crypto with cards, bank transfer and local payment methods.",
                icon: <FiatDepositSheetIcon />,
                iconTone: "mint",
                onClick: goFiatDeposit,
                itemTone: "default",
              },
            ]
          : []),
        {
          id: "crypto",
          title: t("home.depositSheetOnChainTitle") || t("more.cryptoDeposit") || "ON-Chain Deposit",
          description: t("home.depositSheetOnChainDesc") || t("trade.depositTips") || "",
          icon: <CryptoDepositSheetIcon />,
          iconTone: "mint",
          onClick: goCryptoDeposit,
          itemTone: "default",
        },
        ...(hideThirdPartyBuyItem ? [] : [thirdPartyDepositRow]),
      ]
    : [
        {
          id: "crypto",
          title: t("home.depositSheetOnChainTitle") || t("more.cryptoDeposit") || "ON-Chain Deposit",
          description: t("home.depositSheetOnChainDesc") || t("trade.depositTips") || "",
          icon: <CryptoDepositSheetIcon />,
          iconTone: "mint",
          onClick: goCryptoDeposit,
          itemTone: "default",
        },
        ...(hideThirdPartyBuyItem ? [] : [thirdPartyDepositRow]),
      ];

  return (
    <Drawer
      className={styles["deposit-sheet"]}
      placement="bottom"
      width="100vw"
      height="auto"
      zIndex={1500}
      open={open}
      onClose={handleDrawerClose}
      closable={false}
      maskClosable
      destroyOnClose
      footer={null}
      title={null}
      maskStyle={{ background: "rgba(0, 0, 0, 0.55)" }}
    >
      <div className={styles["deposit-sheet__inner"]}>
        <div className={styles["deposit-sheet__grabber"]} />
        <div className={styles["deposit-sheet__header"]}>
          <span className={styles["deposit-sheet__title"]}>{t("more.deposit") || "Deposit"}</span>
          {showFiatDepositOption && primaryFiatForDeposit ? (
            <div className={styles["deposit-sheet__currency-wrap"]} ref={fiatDropdownWrapRef}>
              <button
                type="button"
                className={styles["deposit-sheet__currency"]}
                onClick={() => {
                  if (fiatOptionsForDeposit.length > 1) {
                    setFiatDropdownOpen((o) => !o);
                  }
                }}
              >
                <DepositFiatIcon
                  code={primaryFiatForDeposit}
                  className={styles["deposit-sheet__currency-icon"]}
                  optionIndex={selectedFiatIndex >= 0 ? selectedFiatIndex : 0}
                />
                <span className={styles["deposit-sheet__currency-code"]}>{primaryFiatForDeposit}</span>
                {fiatOptionsForDeposit.length > 1 ? (
                  <span
                    className={`${styles["deposit-sheet__currency-arrow"]} ${
                      fiatDropdownOpen ? styles["deposit-sheet__currency-arrow--open"] : ""
                    }`}
                  >
                    <DepositSheetChevron />
                  </span>
                ) : null}
              </button>
              {fiatDropdownOpen && fiatOptionsForDeposit.length > 1 ? (
                <div className={styles["deposit-sheet__currency-menu"]}>
                  {fiatOptionsForDeposit.map((code, index) => (
                    <button
                      key={code}
                      type="button"
                      className={`${styles["deposit-sheet__currency-item"]} ${
                        primaryFiatForDeposit === code ? styles["deposit-sheet__currency-item--active"] : ""
                      }`}
                      onClick={() => {
                        setPrimaryFiatForDeposit(code);
                        setFiatDropdownOpen(false);
                      }}
                    >
                      <DepositFiatIcon code={code} className={styles["deposit-sheet__currency-item-icon"]} optionIndex={index} />
                      <span>{code.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className={styles["deposit-sheet__list"]}>
          {depositOptions.map((item) => (
            <button key={item.id} type="button" className={styles["deposit-sheet__item"]} onClick={wrapDepositOptionClick(item, item.onClick)}>
              <span
                className={
                  item.iconTone === "gold"
                    ? `${styles["deposit-sheet__item-icon"]} ${styles["deposit-sheet__item-icon--gold"]}`
                    : `${styles["deposit-sheet__item-icon"]} ${styles["deposit-sheet__item-icon--mint"]}`
                }
              >
                {item.icon}
              </span>
              <span className={styles["deposit-sheet__item-content"]}>
                <span className={styles["deposit-sheet__item-title"]}>{item.title}</span>
                <span className={styles["deposit-sheet__item-desc"]}>{item.description}</span>
              </span>
              <span className={styles["deposit-sheet__item-arrow"]}>
                <DepositSheetArrow />
              </span>
            </button>
          ))}
        </div>
      </div>
    </Drawer>
  );
}

export default HomeDepositDrawer;
